import { useState, useRef, useEffect } from "react";
import { select, scaleBand, axisBottom, axisLeft, scaleLinear, map, max, range } from "d3";
import { toPng } from "html-to-image";
import { AiOutlineSwap, AiOutlineDownload, AiOutlineCamera } from "react-icons/ai";

function wrap(text, width) {
  text.each(function () {
    var text = select(this),
      words = text.text().split(/\s+/).reverse(),
      word,
      line = [],
      lineNumber = 0,
      lineHeight = 1.1, // ems
      y = text.attr("y"),
      dy = parseFloat(text.attr("dy")),
      tspan = text
        .text(null)
        .append("tspan")
        .attr("x", 0)
        .attr("y", y)
        .attr("dy", dy + "em");
    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = text
          .append("tspan")
          .attr("x", 0)
          .attr("dy", lineHeight + "em")
          .text(word);
      }
    }
  });
}

export default function BarChart(props) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  // State for editable labels and axis direction
  const [xLabel, setXLabel] = useState("X Axis Label");
  const [yLabel, setYLabel] = useState("Y Axis Label");
  const [isEditing, setIsEditing] = useState(false);
  const [swapAxes, setSwapAxes] = useState(false); // New state for toggling axes
  const [sortDirection, setSortDirection] = useState(null); // null, 'asc', or 'desc'

  const maxLabelLength = props.datasets && props.datasets.length > 0
    ? Math.max(...props.datasets.map(d => (d.x ? String(d.x).length : 0)))
    : 0;

  let chartWrapperHeight = 300;
  if (!swapAxes) {
    if (maxLabelLength > 100) {
      chartWrapperHeight = 420;
    } else if (maxLabelLength > 60) {
      chartWrapperHeight = 360;
    } else if (maxLabelLength > 30) {
      chartWrapperHeight = 330;
    }
  }

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const updateSize = () => {
      if (!wrapperRef.current) return;
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setChartSize({ width, height });
    };

    updateSize();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateSize);
      observer.observe(element);
    }

    window.addEventListener("resize", updateSize);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  useEffect(() => {
    if (!props.datasets) return;
    const svg = select(svgRef.current);

    // Determine which axes to use based on the toggle
    const x = swapAxes ? (d) => d.y : (d) => d.x;
    const y = swapAxes ? (d) => d.x : (d) => d.y;

    // Sort data if sortDirection is set
    let data = [...props.datasets];
    if (sortDirection) {
        data.sort((a, b) => {
            // Always sort by the numeric value (y) regardless of orientation
            const valA = Number(a.y);
            const valB = Number(b.y);
            
            // Apply sort direction
            if (sortDirection === 'asc') {
                return valA - valB;
            } else {
                return valB - valA;
            }
        });
    }

    if (props.valueType === "%") {
      const total = data.reduce((acc, d) => acc + (Number(d.y) || 0), 0);
      data = data.map(d => ({
        ...d,
        y: total > 0 ? parseFloat(((Number(d.y) || 0) / total * 100).toFixed(1)) : 0
      }));
    }

    const X = map(data, x);
    const Y = map(data, y);
    const marginTop = 20;
    const marginRight = 20;

    const maxLabelLengthVal = props.datasets && props.datasets.length > 0
      ? Math.max(...props.datasets.map(d => (d.x ? String(d.x).length : 0)))
      : 0;

    let marginBottom = 50;
    if (!swapAxes) {
      if (maxLabelLengthVal > 100) {
        marginBottom = 160;
      } else if (maxLabelLengthVal > 60) {
        marginBottom = 120;
      } else if (maxLabelLengthVal > 30) {
        marginBottom = 90;
      } else {
        marginBottom = 60;
      }
    }
    const marginLeft = 60;
    const width = chartSize.width || props.width || 0;
    const height = chartSize.height || props.height || 0;
    if (width <= 0 || height <= 0) return;

    // Update domains after sorting
    const maxVal = max(swapAxes ? X : Y) || 1;
    const paddedMax = props.valueType === "%" ? Math.min(100, maxVal * 1.05) : maxVal * 1.05;

    const xDomain = swapAxes ? [0, paddedMax] : Array.from(new Set(X));
    const yDomain = swapAxes ? Array.from(new Set(Y)) : [0, paddedMax];
    const xRange = [marginLeft, width - marginRight];
    const yRange = [height - marginBottom, marginTop];
    const xPadding = 0.1;

    // Scales and Axes
    const xScale = swapAxes ? scaleLinear(xDomain, xRange).nice() : scaleBand(xDomain, xRange).padding(xPadding);
    const yScale = swapAxes ? scaleBand(yDomain, yRange).padding(xPadding) : scaleLinear(yDomain, yRange).nice();
    const xAxis = swapAxes 
      ? axisBottom(xScale).ticks(width / 80).tickFormat(d => props.valueType === "%" ? `${d}%` : d) 
      : axisBottom(xScale).tickSizeOuter(0);
    const yAxis = swapAxes 
      ? axisLeft(yScale).tickSizeOuter(0) 
      : axisLeft(yScale).tickFormat(d => props.valueType === "%" ? `${d}%` : d);

    // Clear previous content
    svg.selectAll(".x-axis > *").remove();
    svg.selectAll(".y-axis > *").remove();
    svg.selectAll(".content > *").remove();

    // Render Y Axis
    svg
      .select(".y-axis")
      .append("g")
      .attr("transform", `translate(${marginLeft},0)`)
      .call(yAxis)
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g
          .append("text")
          .attr("x", -marginLeft + 10)
          .attr("y", 10)
          .attr("fill", "currentColor")
          .attr("text-anchor", "start")
          .attr("font-family", "Noto Sans")
          .text(swapAxes ? xLabel : yLabel) // Swap labels
      );

    // Render X Axis
    const xAxisG = svg
      .select(".x-axis")
      .append("g")
      .attr("transform", `translate(0,${height - marginBottom})`)
      .call(xAxis);

    xAxisG.selectAll("text")
      .style("font-size", "11px")
      .style("font-family", "Noto Sans");

    if (!swapAxes) {
      xAxisG.selectAll("text")
        .call(wrap, Math.min(xScale.bandwidth(), 100));
    }

    // Render X-Axis Label
    svg
      .select(".x-axis")
      .append("text")
      .attr("x", (width - marginLeft - marginRight) / 2 + marginLeft)
      .attr("y", height - 10) // positioned below the ticks
      .attr("text-anchor", "middle")
      .attr("font-family", "Noto Sans")
      .attr("font-size", "12px")
      .attr("fill", "currentColor")
      .text(swapAxes ? yLabel : xLabel); // Swap labels

    // Render Bars
    const I = range(X.length);
    const bar = svg
      .select(".content")
      .append("g")
      .attr("fill", "rgb(35,97,160)")
      .selectAll("rect")
      .data(I)
      .join("rect")
      .attr("x", (i) => (swapAxes ? xScale(0) : xScale(X[i]) + xScale.bandwidth() / 2 - 50))
      .attr("y", (i) => (swapAxes ? yScale(Y[i]) : yScale(Y[i])))
      .attr("width", (i) => (swapAxes ? xScale(X[i]) - xScale(0) : Math.min(xScale.bandwidth(), 100)))
      .attr("height", (i) => (swapAxes ? Math.min(yScale.bandwidth(), 30) : yScale(0) - yScale(Y[i])));

    // Add value labels
    svg.select(".content")
        .append("g")
        .selectAll("text")
        .data(I)
        .join("text")
        .attr("x", (i) => {
            if (swapAxes) {
                return xScale(X[i]) + 5; // Position after the bar end for horizontal
            } else {
                return xScale(X[i]) + xScale.bandwidth() / 2; // Center on bar for vertical
            }
        })
        .attr("y", (i) => {
            if (swapAxes) {
                return yScale(Y[i]) + yScale.bandwidth() / 2 + 5; // Center on bar for horizontal
            } else {
                return yScale(Y[i]) - 5; // Position above bar for vertical
            }
        })
        .attr("text-anchor", swapAxes ? "start" : "middle")
        .attr("font-family", "Noto Sans")
        .attr("font-size", "11px")
        .attr("fill", "black")
        .text(i => props.valueType === "%" ? `${Y[i]}%` : Y[i]);

  }, [props.datasets, props.valueType, props.width, props.height, chartSize.width, chartSize.height, xLabel, yLabel, swapAxes, sortDirection]);

  // Handle Edit Toggle
  const handleEditToggle = () => {
    setIsEditing((prev) => !prev);
  };

  // Handle Save
  const handleSave = () => {
    setIsEditing(false);
  };

  // Handle Export
  const handleExport = () => {
    const chart = wrapperRef.current;

    toPng(chart, { cacheBust: true })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = "chart.png";
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error("Error exporting chart as image:", err);
      });
  };

  // Handle Axis Swap
  const handleSwapAxes = () => {
    setSwapAxes((prev) => !prev);
  };

  // Add sort handler
  const handleSort = () => {
    setSortDirection(current => {
      if (!current) return 'asc';
      if (current === 'asc') return 'desc';
      return null;
    });
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        <button
          onClick={handleEditToggle}
          style={{
            padding: "0.375rem 1.125rem",
            height: "30px",
            fontSize:"10px",
            backgroundColor: "#2361a0",
            color: "#fff",
            fontWeight:"400",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          {/* fontWeight="400"
          fontSize="10px"
          className="ml-2"
          size="small"
          variant="outlined"
          buttonColor="#F2F4F4" */}
          {isEditing ? "Cancel" : "Edit Axis Labels"}
        </button>
        <button
          onClick={handleSort}
          style={{
            padding: "0.375rem 1.125rem",
            backgroundColor: "#2361a0",
            color: "#fff",
            fontSize:"10px",
            height: "30px",
            border: "none",
            fontWeight:"400",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          {sortDirection === 'asc' ? '↑ Sort' : sortDirection === 'desc' ? '↓ Sort' : '⇅ Sort'}
        </button>
        {!isEditing && (
          <button
            onClick={handleExport}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#2361a0",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "48px",
              height: "30px"
            }}
          >
            <AiOutlineCamera style={{ fontSize: "1.5rem" }} />
          </button>
        )}
      </div>
      {isEditing && (
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <label style={{ fontFamily: "Noto Sans", fontSize: "0.9rem" }}>
            X Axis Label:
            <input
              type="text"
              value={xLabel}
              onChange={(e) => setXLabel(e.target.value)}
              style={{
                marginLeft: "0.5rem",
                padding: "0.3rem",
                fontSize: "0.9rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
            {/* Swap Axes Icon */}
            <AiOutlineSwap
              onClick={handleSwapAxes}
              style={{
                cursor: "pointer",
                fontSize: "1.5rem",
                color: "#2361a0",
                marginLeft:"2rem"
              }}
              title="Swap Axes"
            />
          </label>
          <label style={{ fontFamily: "Noto Sans", fontSize: "0.9rem" }}>
            Y Axis Label:
            <input
              type="text"
              value={yLabel}
              onChange={(e) => setYLabel(e.target.value)}
              style={{
                marginLeft: "0.5rem",
                padding: "0.3rem",
                fontSize: "0.9rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </label>
          <button
            onClick={handleSave}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#2361a0",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Save
          </button>
        </div>
      )}
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          height: `${chartWrapperHeight}px`,
          marginBottom: "2rem",
          margin: "0 auto",
          backgroundColor: "white"
        }}
      >
        <svg ref={svgRef} style={{ width: "100%", height: "110%" }}>
          <g className="x-axis" />
          <g className="y-axis" />
          <g className="content" />
        </svg>
      </div>
    </>
  );
}